const DEFAULT_ENVIRONMENT = "development";

export type Environment = "development" | "staging" | "production" | "test";

export interface RuntimeConfig {
  adminApiUrl: string;
  centrifugoUrl: string;
  environment: Environment;
  /**
   * Base URL of the OpenObserve UI (e.g. `https://observe.breakingthelines.dev`),
   * or undefined when not configured. Unlike adminApiUrl/centrifugoUrl this has
   * NO hardcoded fallback: prod carries no external ingress for OpenObserve
   * today (see infrastructure deploy/prod/k8s/platform/openobserve — a Service,
   * no Ingress), so a default value here would produce a live-looking link
   * that 404s for a prod admin. Absent means absent; the Audit page's
   * OpenObserve links row is not rendered at all when this is undefined.
   */
  ooBaseUrl?: string;
}

/**
 * Runtime config injected by the Bun static server (server.ts) as
 * `window.__ENV` before the SPA bundle executes. This is how a SINGLE built
 * image serves multiple environments: the image bakes NO `VITE_ADMIN_API_URL`,
 * and the per-env value is supplied at container start via the `ADMIN_API_URL`
 * env var (see server.ts + the staging compose / prod deployment). The VITE_*
 * vars remain the fallback for local `vite dev`/`vite preview`, where there is
 * no Bun server to inject `window.__ENV`.
 */
interface InjectedEnv {
  adminApiUrl?: string;
  centrifugoUrl?: string;
  environment?: Environment;
  ooBaseUrl?: string;
}

declare global {
  interface Window {
    __ENV?: InjectedEnv;
  }
}

function readEnv(): RuntimeConfig {
  const env = import.meta.env;
  const injected = typeof window !== "undefined" ? window.__ENV : undefined;
  return {
    adminApiUrl: injected?.adminApiUrl ?? env.VITE_ADMIN_API_URL ?? "http://localhost:9090",
    centrifugoUrl:
      injected?.centrifugoUrl ??
      env.VITE_CENTRIFUGO_URL ??
      "ws://localhost:8086/connection/websocket",
    environment: injected?.environment ?? env.VITE_ENVIRONMENT ?? DEFAULT_ENVIRONMENT,
    ooBaseUrl: injected?.ooBaseUrl ?? env.VITE_OO_BASE_URL ?? undefined,
  } as RuntimeConfig;
}

export const runtimeConfig = readEnv();

// Public-site base for "view on site" links. Derived from the resolved
// environment rather than a separate env var, so it needs no extra container
// injection. Anything that isn't explicitly development/staging resolves to
// production — a wrong-but-live link (this used to be a hardcoded
// http://localhost:3000, which 404s for every admin on prod) is the failure
// mode to avoid.
export function siteBaseUrl(): string {
  switch (runtimeConfig.environment) {
    case "development":
      return "http://localhost:3000";
    case "staging":
      return "https://breakingthelines.dev";
    default:
      return "https://breakingthelines.com";
  }
}

// Creator-app base, for handover instructions that tell an advertiser where to
// sign in. Derived from the same resolved environment as siteBaseUrl() rather
// than a second injected var, for the same reason: an operator pasting a
// staging URL into a message to a real advertiser is the failure mode, and a
// URL that is wrong-but-live is worse than one that is merely wrong.
export function studioBaseUrl(): string {
  switch (runtimeConfig.environment) {
    case "development":
      return "http://localhost:3001";
    case "staging":
      return "https://studio.breakingthelines.dev";
    default:
      return "https://studio.breakingthelines.com";
  }
}

// Media CDN base, ending in "/media" the same way platform's
// `getPublicRuntimeConfig().mediaCdnBase` does, because the design-system's
// entity-asset resolver takes a base in exactly that shape and the admin
// preview has to build the SAME addresses the app builds or it is previewing
// something nobody sees.
//
// Derived from the resolved environment rather than a second injected var, for
// the reason siteBaseUrl() gives: one fewer thing to set per container. The
// values mirror platform's own config (`app/lib/config.ts`) - production
// defaults to the `.app` CDN, staging and local override to `.dev`.
export function mediaCdnBaseUrl(): string {
  switch (runtimeConfig.environment) {
    case "development":
    case "staging":
      return "https://cdn.breakingthelines.dev/media";
    default:
      return "https://cdn.breakingthelines.app/media";
  }
}

// OpenObserve deep links for the Audit page's "view in OpenObserve" row.
//
// admin-service ships every log line, including the audited-mutation lines
// internal/audit/audit.go's Recorder.ship writes, to ONE OpenObserve org and
// stream: infrastructure's otel-collector config points the OTLP exporter at
// `http://openobserve:5080/api/default` (org "default"), and admin-service
// sets no per-stream OTLP attribute, so everything lands in that org's
// default logs stream. Two fields distinguish an audited mutation from the
// rest of admin-service's logs there:
//   - `service`: the slog attribute internal/telemetry/logging.go stamps on
//     every line (admin-service's own name for itself, "admin-service").
//   - the literal message "admin mutation", which Recorder.ship writes for
//     every event Record() is given, success or failure.
const OPENOBSERVE_ORG = "default";
const OPENOBSERVE_STREAM = "default";
const OPENOBSERVE_SERVICE_NAME = "admin-service";

export interface OpenObserveViews {
  /** Just the audited-mutation lines: message = "admin mutation". */
  adminMutations: string;
  /** Every admin-service log line, mutations included. */
  serviceLogs: string;
}

/**
 * Deep links into the OpenObserve Logs explorer, or undefined when no
 * OpenObserve base URL is configured.
 *
 * Undefined is the expected value in prod today: infrastructure's prod
 * overlay gives OpenObserve a Service but no Ingress (unlike staging's
 * `observe.breakingthelines.dev`), so there is no URL a prod admin's browser
 * could reach. The Audit page hides its OpenObserve links row entirely in
 * that case rather than rendering a link to nowhere.
 */
export function openObserveViews(): OpenObserveViews | undefined {
  const base = runtimeConfig.ooBaseUrl;
  if (!base) return undefined;

  const origin = base.replace(/\/+$/, "");
  const buildUrl = (query: string) => {
    const params = new URLSearchParams({
      stream_type: "logs",
      stream: OPENOBSERVE_STREAM,
      org_identifier: OPENOBSERVE_ORG,
      query,
    });
    return `${origin}/web/logs?${params.toString()}`;
  };

  return {
    adminMutations: buildUrl(`service='${OPENOBSERVE_SERVICE_NAME}' AND message='admin mutation'`),
    serviceLogs: buildUrl(`service='${OPENOBSERVE_SERVICE_NAME}'`),
  };
}
